'use client';

import { useQueryClient } from '@tanstack/react-query';
import Head from 'next/head';
import Script from 'next/script';
import { useLocale } from 'next-intl';
import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react';
import type {
  AddToCartData,
  CheckoutStepData,
  ConfiguratorInteractionData,
  ErrorData,
  ExperimentInteractionData,
  FAQInteractionData,
  FormSubmissionData,
  GA4ContextType,
  InteractionData,
  LoginData,
  MenuNavigationData,
  PurchaseData,
  RemoveFromCartData,
  SearchData,
  SelectItemData,
  SocialAuthData,
  ViewCartData,
  ViewItemData,
} from '@/ui/models/ga4';
import { GTM_ID } from '@/core/constants/env';
import useAuth from '@/core/hooks/use-auth';
import { usePathname } from '@/core/i18n/routing';
import {
  type BffSubscription,
  selectPrimaryBffSubscription,
} from '@/core/models/bff-subscriptions';
import { queryKey as bffSubscriptionsQueryKey } from '@/core/queries/bff/subscriptions/use-bff-subscriptions-query';
import * as GA4Service from '@/core/services/ga4/ga4-service';
import {
  clearPostBookingGa4Context,
  shouldSkipBookingDetailPageView,
} from '@/core/utils/booking/booking-ga4-context';
import isBookingDetailPathname from '@/core/utils/is-booking-detail-pathname';
import isBookingManagePathname from '@/core/utils/is-booking-manage-pathname';
import isBookingRootPathname from '@/core/utils/is-booking-root-pathname';
import { useEvaContext } from '@/ui/context/eva';

const GA4Context = createContext<GA4ContextType | null>(null);

export const useGA4 = (): GA4ContextType => {
  const context = useContext(GA4Context);
  if (!context) {
    throw new Error('useGA4 must be used within a GA4Provider');
  }
  return context;
};

export const GA4Provider = ({ children }: PropsWithChildren) => {
  const locale = useLocale();
  const pathname = usePathname();
  const queryClient = useQueryClient();
  const { user, status } = useAuth();
  const { organization } = useEvaContext();
  // Track latest page_view and a possible in-flight one to gate other events
  const lastTrackedPathRef = useRef<string | undefined>(undefined);
  const inFlightPathRef = useRef<string | undefined>(undefined);
  const pageViewInFlightRef = useRef<Promise<void> | null>(null);
  const previousPathnameRef = useRef(pathname);

  const getUserMembershipType = useCallback(() => {
    if (status === 'authenticated') return 'existing member';
    return 'new member';
  }, [status]);

  const getCountryCode = useCallback((): string => {
    const code = organization?.countryId;
    return typeof code === 'string' && code.length > 0 ? code : 'NL';
  }, [organization?.countryId]);

  const getSubscriptionTypeForTracking = useCallback((): string | undefined => {
    if (status !== 'authenticated') return undefined;

    const subscriptions = queryClient.getQueryData<BffSubscription[]>(
      bffSubscriptionsQueryKey({ userId: 'me' }),
    );
    const primary = selectPrimaryBffSubscription(subscriptions);

    return primary?.description ?? primary?.name;
  }, [queryClient, status]);

  const trackPageView = useCallback(
    async (pathnameToTrack?: string): Promise<void> => {
      const targetPath = pathnameToTrack || pathname;

      // Deduplicate by path and in-flight path
      if (
        lastTrackedPathRef.current === targetPath ||
        inFlightPathRef.current === targetPath
      ) {
        return;
      }

      inFlightPathRef.current = targetPath;
      const promise = GA4Service.trackPageView({
        pathname: targetPath,
        country: getCountryCode(),
        locale: locale.replace('-', '_'),
        user: {
          id: undefined,
          email: user?.personalData?.email || undefined,
          isLoggedIn: status === 'authenticated',
          membershipType: getUserMembershipType(),
          subscriptionType: getSubscriptionTypeForTracking(),
        },
      })
        .then(() => {
          lastTrackedPathRef.current = targetPath;
        })
        .catch(error => {
          console.error('[GA4] Error sending page_view:', error);
        })
        .finally(() => {
          if (inFlightPathRef.current === targetPath) {
            inFlightPathRef.current = undefined;
          }
          if (pageViewInFlightRef.current === promise) {
            pageViewInFlightRef.current = null;
          }
        });

      pageViewInFlightRef.current = promise;
      await promise;
    },
    [
      pathname,
      getCountryCode,
      locale,
      user?.personalData?.email,
      status,
      getUserMembershipType,
      getSubscriptionTypeForTracking,
    ],
  );

  // Auto-trigger page_view on pathname change. Booking uses virtual step paths instead.
  useLayoutEffect(() => {
    const previousPathname = previousPathnameRef.current;
    previousPathnameRef.current = pathname;

    if (
      isBookingDetailPathname(previousPathname) &&
      !isBookingDetailPathname(pathname)
    ) {
      clearPostBookingGa4Context();
    }

    if (isBookingRootPathname(pathname)) return;
    // The manage path carries the booking UUID: never report it (MOA-2439).
    if (isBookingManagePathname(pathname)) return;
    if (shouldSkipBookingDetailPageView(pathname)) return;
    void trackPageView(pathname);
  }, [pathname, trackPageView]);

  const waitForPageView = useCallback((): Promise<void> => {
    const targetPath = pathname;
    if (
      lastTrackedPathRef.current !== targetPath &&
      inFlightPathRef.current !== targetPath &&
      !pageViewInFlightRef.current
    ) {
      const promise = trackPageView(targetPath);
      return promise;
    }
    return pageViewInFlightRef.current ?? Promise.resolve();
  }, [pathname, trackPageView]);

  const value: GA4ContextType = useMemo(
    () => ({
      trackPageView,
      trackMenuNavigation: (data: MenuNavigationData) => {
        void waitForPageView().then(() => GA4Service.trackMenuNavigation(data));
      },
      trackFormSubmission: (data: FormSubmissionData) => {
        void waitForPageView().then(() => GA4Service.trackFormSubmission(data));
      },
      trackViewCart: (data: ViewCartData) => {
        void waitForPageView().then(() => GA4Service.trackViewCart(data));
      },
      trackViewItem: (data: ViewItemData) => {
        void waitForPageView().then(() => GA4Service.trackViewItem(data));
      },
      trackSelectItem: (data: SelectItemData) => {
        void waitForPageView().then(() => GA4Service.trackSelectItem(data));
      },
      trackAddToCart: (data: AddToCartData) => {
        void waitForPageView().then(() => GA4Service.trackAddToCart(data));
      },
      trackRemoveFromCart: (data: RemoveFromCartData) => {
        void waitForPageView().then(() => GA4Service.trackRemoveFromCart(data));
      },
      trackConfiguratorInteraction: (data: ConfiguratorInteractionData) => {
        void waitForPageView().then(() =>
          GA4Service.trackConfiguratorInteraction(data),
        );
      },
      trackCheckoutStep: (data: CheckoutStepData) => {
        void waitForPageView().then(() => GA4Service.trackCheckoutStep(data));
      },
      trackFAQInteraction: (data: FAQInteractionData) => {
        void waitForPageView().then(() => GA4Service.trackFAQInteraction(data));
      },
      trackInteraction: (data: InteractionData) => {
        void waitForPageView().then(() => GA4Service.trackInteraction(data));
      },
      trackPurchase: (data: PurchaseData) => {
        void waitForPageView().then(() => GA4Service.trackPurchase(data));
      },
      trackLogin: async (data: LoginData): Promise<void> => {
        await waitForPageView();
        await GA4Service.trackLogin(data);
      },
      trackSocialAuth: (data: SocialAuthData) => {
        void waitForPageView().then(() => GA4Service.trackSocialAuth(data));
      },
      trackSearch: (data: SearchData) => {
        void waitForPageView().then(() => GA4Service.trackSearch(data));
      },
      trackError: (data: ErrorData) => {
        void waitForPageView().then(() => GA4Service.trackError(data));
      },
      trackOutOfStockSubmit: () => {
        void waitForPageView().then(() => GA4Service.trackOutOfStockSubmit());
      },
      trackViewItemList: () => {
        void waitForPageView().then(() => GA4Service.trackViewItemList());
      },
      trackExperimentInteraction: (data: ExperimentInteractionData) => {
        void waitForPageView().then(() =>
          GA4Service.trackExperimentInteraction(data),
        );
      },
    }),
    [trackPageView, waitForPageView],
  );

  return (
    <GA4Context.Provider value={value}>
      <Head>
        <link rel="preconnect" href="https://www.googletagmanager.com" />
      </Head>

      <Script
        id="gtm-script"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: `
          (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
          new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
          j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
          'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
          })(window,document,'script','dataLayer','${GTM_ID}');
          `,
        }}
      />

      {children}
    </GA4Context.Provider>
  );
};

export default GA4Provider;
